import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import PlayerTable from '../components/PlayerTable';
import InitialsAvatar from '../components/InitialsAvatar';
import InsightCard from '../components/InsightCard';
import { formatAppearances, formatPlayerGPG } from '../utils/formatters';
import { generateDashboardInsight } from '../utils/insights';

export default function Dashboard() {
  const navigate = useNavigate();

  const { data: dashData, loading: dashLoading } = useApi('/api/dashboard-stats');
  const { data: scorersData, loading: scorersLoading } = useApi('/api/top-scorers?limit=50');
  const { data: gpgData } = useApi('/api/top-goals-per-game?limit=1&min_appearances=5');

  const stats = dashData?.stats;
  const topScorers = scorersData?.stats || [];
  const topGPGPlayer = gpgData?.stats?.[0];
  const insight = generateDashboardInsight(topScorers);

  const leagueTopScorers = {};
  topScorers.forEach((p) => {
    if (!leagueTopScorers[p.league_name]) {
      leagueTopScorers[p.league_name] = p;
    }
  });

  const loading = dashLoading || scorersLoading;

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-5">
          <div>
            <div className="h-3 w-44 bg-surface-container-highest rounded mb-3" />
            <div className="h-9 w-72 max-w-full bg-surface-container-highest rounded-xl mb-3" />
            <div className="h-4 w-96 max-w-full bg-surface-container rounded-lg" />
          </div>
          <div className="flex gap-3">
            <div className="h-16 w-32 bg-surface-container rounded-xl border border-outline-variant/30" />
            <div className="h-16 w-32 bg-surface-container rounded-xl border border-outline-variant/30" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="md:col-span-2 h-48 bg-surface-container rounded-xl border border-outline-variant/30" />
          <div className="h-48 bg-surface-container rounded-xl border border-outline-variant/30" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-surface-container rounded-xl border border-outline-variant/30 p-6 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-surface-container-highest rounded-lg" />
            ))}
          </div>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-14 bg-surface-container rounded-xl border border-outline-variant/30" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const topScorer = stats?.topScorer;
  const efficiencyKing = topGPGPlayer || stats?.topGoalsPerGame;

  return (
    <div className="space-y-8">
      {/* Hero */}
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-5">
        <div>
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-2">
            Non-League Goal Analytics
          </p>
          <h2 className="font-expanded font-headline text-3xl sm:text-4xl font-bold tracking-tight text-on-surface mb-2">
            Below The Radar
          </h2>
          <p className="text-on-surface-variant text-sm max-w-xl">
            English non-league goal scorers. Real-time Step 5-7 analytics.
          </p>
        </div>
        <div className="flex gap-3">
          <div className="bg-surface-container px-5 py-3 rounded-xl border border-outline-variant/30">
            <p className="font-label text-[11px] font-semibold text-on-surface-variant uppercase tracking-[0.12em] mb-1.5">Active Leagues</p>
            <p className="font-expanded text-2xl font-headline font-bold text-secondary tabular-nums leading-none">{stats?.totalLeagues || 0}</p>
          </div>
          <div className="bg-surface-container px-5 py-3 rounded-xl border border-outline-variant/30">
            <p className="font-label text-[11px] font-semibold text-on-surface-variant uppercase tracking-[0.12em] mb-1.5">Players Tracked</p>
            <p className="font-expanded text-2xl font-headline font-bold text-primary tabular-nums leading-none">{stats?.totalPlayers || 0}+</p>
          </div>
        </div>
      </header>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Top Scorer Hero */}
        <section className="md:col-span-2 relative overflow-hidden bg-surface-container rounded-xl border border-outline-variant/30">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.06] to-transparent pointer-events-none" />
          <div className="relative p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="shrink-0 self-center sm:self-auto">
              <InitialsAvatar name={topScorer?.player_name} clubName={topScorer?.club_name} size="xl" />
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <span className="inline-block px-2.5 py-1 bg-primary/10 text-primary font-label text-[11px] font-semibold rounded-full mb-2.5 uppercase tracking-[0.12em] border border-primary/20">
                Overall Top Scorer
              </span>
              <h3 className="font-expanded text-2xl font-headline font-bold text-on-surface mb-1">
                {topScorer?.player_name || 'N/A'}
              </h3>
              <p className="text-on-surface-variant text-sm mb-4">
                {topScorer?.club_website ? (
                  <a href={topScorer.club_website} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors duration-150 underline decoration-outline-variant/30 underline-offset-2 hover:decoration-primary" onClick={(e) => e.stopPropagation()}>
                    {topScorer.club_name}
                  </a>
                ) : topScorer?.club_name}
                <span className="mx-2 text-outline">&bull;</span> {topScorer?.league_name}
              </p>
              <div className="flex flex-wrap items-end justify-center sm:justify-start gap-x-5 gap-y-3 sm:gap-x-6 lg:gap-x-8">
                <div>
                  <p className="font-label text-[11px] font-semibold text-on-surface-variant uppercase tracking-[0.12em] mb-1.5">Goals</p>
                  <p className="font-expanded text-4xl sm:text-5xl font-headline font-bold text-primary tabular-nums leading-none">{topScorer?.total_goals || 0}</p>
                </div>
                <div>
                  <p className="font-label text-[11px] font-semibold text-on-surface-variant uppercase tracking-[0.12em] mb-1.5">Appearances</p>
                  <p className="font-expanded text-2xl font-headline font-bold text-on-surface tabular-nums leading-none pb-0.5">{formatAppearances(topScorer)}</p>
                </div>
                <div>
                  <p className="font-label text-[11px] font-semibold text-on-surface-variant uppercase tracking-[0.12em] mb-1.5">GPG Ratio</p>
                  <p className="font-expanded text-2xl font-headline font-bold text-secondary tabular-nums leading-none pb-0.5">
                    {formatPlayerGPG(topScorer)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Efficiency King */}
        <section className="bg-surface-container rounded-xl p-5 sm:p-6 relative overflow-hidden flex flex-col justify-between border border-outline-variant/30">
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/[0.05] to-transparent pointer-events-none" />
          <div className="relative">
            <span className="inline-block px-2.5 py-1 bg-secondary/10 text-secondary font-label text-xs font-semibold rounded-full mb-3 uppercase tracking-[0.12em] border border-secondary/20">
              Efficiency King
            </span>
            <h4 className="font-expanded text-xl font-headline font-bold text-on-surface mb-0.5">
              {efficiencyKing?.player_name || 'N/A'}
            </h4>
            <p className="text-on-surface-variant text-sm mb-5">{efficiencyKing?.club_name}</p>
            <div className="flex items-baseline gap-2">
              <span className="font-expanded text-5xl font-headline font-bold text-secondary tabular-nums leading-none">
                {efficiencyKing?.goals_per_game ? Number(efficiencyKing.goals_per_game).toFixed(2) : '0.00'}
              </span>
              <span className="font-label text-xs font-semibold uppercase tracking-[0.12em] text-on-surface-variant">GPG</span>
            </div>
          </div>
          <div className="relative h-1.5 w-full bg-surface-container-highest rounded-full overflow-hidden mt-5">
            <div className="h-full rounded-full bg-secondary transition-all duration-200" style={{ width: `${Math.min((efficiencyKing?.goals_per_game || 0) * 50, 100)}%` }} />
          </div>
        </section>
      </div>

      {/* Main Data: Leaderboard + League Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Player Power Rankings */}
        <section className="lg:col-span-2 bg-surface-container rounded-xl overflow-hidden border border-outline-variant/30">
          <div className="px-5 sm:px-6 pt-5 pb-4 border-b border-outline-variant/30">
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-1">Top 5</p>
            <h3 className="font-expanded font-headline text-lg font-bold text-on-surface">Player Power Rankings</h3>
          </div>
          <PlayerTable players={topScorers} limit={5} />
          <div className="px-5 py-3 text-center border-t border-outline-variant/30">
            <button
              onClick={() => navigate('/players')}
              className="inline-flex min-h-[40px] items-center justify-center gap-1 px-4 font-label text-xs font-semibold uppercase tracking-[0.12em] text-primary hover:gap-2 transition-all duration-150"
            >
              View All {stats?.totalPlayers || 0}+ Players
              <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </button>
          </div>
        </section>

        {/* Right Sidebar */}
        <div className="space-y-5">
          {/* League Top Scorers */}
          <section className="bg-surface-container rounded-xl p-5 border border-outline-variant/30">
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-1">Across The Leagues</p>
            <h3 className="font-expanded font-headline text-lg font-bold text-on-surface mb-3">League Top Scorers</h3>
            <div>
              {Object.entries(leagueTopScorers).slice(0, 5).map(([leagueName, player], i) => (
                <div
                  key={leagueName}
                  className={`flex items-center justify-between gap-3 min-h-[44px] py-2 group cursor-pointer active:scale-[0.99] transition-transform duration-150 ${i > 0 ? 'border-t border-outline-variant/30' : ''}`}
                  onClick={() => navigate(`/players/${encodeURIComponent(player.player_name)}`)}
                >
                  <div className="min-w-0">
                    <p className={`font-label text-xs font-semibold uppercase tracking-[0.12em] leading-snug ${i === 0 ? 'text-primary' : i === 1 ? 'text-secondary' : 'text-on-surface-variant'}`}>
                      {leagueName}
                    </p>
                    <p className="text-sm font-semibold text-on-surface group-hover:text-primary transition-colors duration-150">
                      {player.player_name}
                    </p>
                  </div>
                  <span className="font-expanded shrink-0 text-xl font-headline font-bold text-on-surface tabular-nums group-hover:text-primary transition-colors duration-150">
                    {player.total_goals}
                  </span>
                </div>
              ))}
              {Object.keys(leagueTopScorers).length > 5 && (
                <div className="pt-3">
                  <button
                    onClick={() => navigate('/leagues')}
                    className="w-full min-h-[40px] border border-outline-variant/40 rounded-xl font-label text-xs font-semibold uppercase tracking-[0.12em] text-on-surface-variant hover:text-on-surface hover:border-outline transition-colors duration-150"
                  >
                    Show {Object.keys(leagueTopScorers).length - 5} More Leagues
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Scout Insight */}
          {insight && (
            <InsightCard
              body={insight.text}
              linkText={insight.linkText}
              onLinkClick={() => navigate(`/players/${encodeURIComponent(insight.playerName)}`)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
